import { Directive, inject, OnInit } from '@angular/core';
import { Store } from '@sama/app-state';
import { DirectiveSuperclass } from '@sama/ng-core';
import {
  getShortcutName,
  toolNameForEventTracking,
} from '../../../cross-team-shared/annotation/shared/abstract-tool';
import { ToggleButton } from '../../../cross-team-shared/components/toggle-button-group/toggle-button-group.component';
import { KeyboardShortcutService } from '../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { ToolsHelperService } from '../../../cross-team-shared/misc-services/tools-helper.service';
import {
  EventTrackingValueWithSource,
  humanize,
} from '../../../cross-team-shared/misc-utils';
import {
  isUniversalOrPointCloudTool,
  ToolName,
} from '../../../cross-team-shared/tools-names';
import {
  ToolConfig,
  toolsConfig,
} from '../../../cross-team-shared/tools.config';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { PointCloudToolName } from '../../state/point-cloud-content-state';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { ToolManager3DService } from '../../tools/tool-manager-3d.service';

@Directive()
export abstract class AbstractMainToolbar3DComponent
  extends DirectiveSuperclass
  implements OnInit
{
  buttons: Array<ToggleButton<ToolName>> = [];
  toolsHelperService = inject(ToolsHelperService);

  protected store = inject(PointCloudContentStore);
  protected toolManager = inject(ToolManager3DService);
  private keyboardShortcutService = inject(KeyboardShortcutService);
  private shellConnector = inject(shellConnectorToken);

  ngOnInit() {
    this.configureButtons();
  }

  selectTool(
    name: PointCloudToolName,
    eventTrackingValue: EventTrackingValueWithSource,
  ) {
    if (this.getSelectedToolStore().state() === name) {
      return;
    }

    this.getSelectedToolStore().set(name);
    this.shellConnector.trackEvent({
      id: 'select_main_tool',
      bucket: this.getTrackingEventBucket(name),
      ...(name === 'Measure' && { sub_bucket: 'frame' }),
      ...eventTrackingValue,
      tool: toolNameForEventTracking(name),
    });
  }

  protected buildButtonFromTool(
    toolConfig: ToolConfig<ToolName>,
  ): ToggleButton<ToolName> {
    return {
      value: toolConfig.name,
      ...toolConfig,
      tooltip: this.getTooltip(toolConfig.name),
    };
  }

  private getTrackingEventBucket(name: PointCloudToolName) {
    switch (name) {
      case 'Measure':
        return 'navigation';
      case 'Edit':
      case 'Select':
        return 'adjustment';
      default:
        return 'annotation';
    }
  }

  private configureButtons() {
    const toolNames = this.getOrderedToolNames();
    toolNames.forEach((toolName) => {
      this.setupKeyboardShortcut(toolName);
      this.toolManager.initTool(toolName);
      this.buttons.push(this.buildButtonFromTool(toolsConfig[toolName]));
    });
    this.getSelectedToolStore().set(toolNames[0]);
  }

  private getOrderedToolNames() {
    const toolNames = this.store.state().readOnly
      ? ['Select', 'Measure']
      : [
          'Edit',
          ...this.toolsHelperService.getAllowedTools(
            this.store.state().output.name,
          ),
          'Measure',
        ];
    return toolNames.filter(isUniversalOrPointCloudTool);
  }

  private getTooltip(toolName: ToolName) {
    const readableShortcut = this.keyboardShortcutService.getTooltipSuffix(
      getShortcutName(toolName),
    );
    if (!readableShortcut) {
      return humanize(toolName);
    }
    return humanize(toolName) + readableShortcut;
  }

  private setupKeyboardShortcut(toolName: PointCloudToolName) {
    this.subscribeTo(
      this.keyboardShortcutService.getMatchingEvent$(getShortcutName(toolName)),
      ({ eventTrackingValue }) => {
        this.selectTool(toolName, eventTrackingValue);
      },
    );
  }

  abstract getSelectedToolStore(): Store<PointCloudToolName>;
}
